import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import Select from 'react-select';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { toast } from 'react-toastify';
import {
  ADMIN_UPDATE_DEMAND,
  ADMIN_UPDATE_DEMAND_STATUS,
  ADMIN_UPDATE_OFFER,
  ADMIN_UPDATE_OFFER_STATUS,
  CITY_BY_STATE,
  STATES_BY_COUNTRY,
} from 'pages/MarketListing/MarketListingGraphQL';

const DEFAULT_COUNTRY_ID = 101;
const DEFAULT_VALIDITY_HOURS = 12;
const MAX_RATE = 999;
const MAX_QUANTITY = 999;

const getNumberValue = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  return value;
};

const getFirstGrade = (row) => row?.grades?.[0] || row?.grade || {};

const getGraphQLErrorMessage = (error) => {
  return (
    error?.graphQLErrors?.[0]?.extensions?.exception?.message ||
    error?.graphQLErrors?.[0]?.message ||
    error?.message ||
    'Something went wrong'
  );
};

const ReactivateListingModal = ({
  isOpen,
  kind,
  mode = 'reactivate',
  row,
  onClose,
  onSuccess,
}) => {
  const isOffer = kind === 'offer';
  const isEdit = mode === 'edit';
  const [formData, setFormData] = useState({
    totalQty: '',
    minimumQty: '',
    rate: '',
    validityInHours: DEFAULT_VALIDITY_HOURS,
    stateId: '',
    cityId: '',
  });

  const countryId = row?.state?.country?.id || DEFAULT_COUNTRY_ID;
  const quantityUnit = row?.actualQuantityUnit || row?.quantityUnit || 'MT';
  const grade = getFirstGrade(row);

  const { data: statesData, loading: statesLoading } = useQuery(
    STATES_BY_COUNTRY,
    {
      variables: { country: countryId },
      skip: !isOpen || !countryId,
      fetchPolicy: 'network-only',
    },
  );

  const [fetchCitiesByState, { loading: citiesLoading }] = useLazyQuery(
    CITY_BY_STATE,
    {
      fetchPolicy: 'network-only',
    },
  );

  const [updateOffer] = useMutation(ADMIN_UPDATE_OFFER);
  const [updateOfferStatus] = useMutation(ADMIN_UPDATE_OFFER_STATUS);
  const [updateDemand] = useMutation(ADMIN_UPDATE_DEMAND);
  const [updateDemandStatus] = useMutation(ADMIN_UPDATE_DEMAND_STATUS);
  const [submitting, setSubmitting] = useState(false);
  const [locationOptions, setLocationOptions] = useState([]);
  const [errors, setErrors] = useState({});

  const states = useMemo(() => statesData?.statesByCountry || [], [statesData]);

  const formatLocationLabel = (cityName, stateName) =>
    `${cityName || ''}${stateName ? `(${stateName})` : ''}`;

  useEffect(() => {
    if (!isOpen || !row) return;
    setFormData({
      totalQty: getNumberValue(row?.totalQty),
      minimumQty: getNumberValue(row?.minimumQty || row?.totalQty),
      rate: getNumberValue(row?.rate),
      validityInHours: getNumberValue(
        row?.validityInHours,
        DEFAULT_VALIDITY_HOURS,
      ),
      stateId: row?.state?.id || '',
      cityId: row?.city?.id || '',
    });
    setErrors({});
  }, [isOpen, row]);

  useEffect(() => {
    if (!isOpen || !states.length) {
      setLocationOptions([]);
      return undefined;
    }

    let isMounted = true;

    const loadLocations = async () => {
      const locations = await Promise.all(
        states.map(async (state) => {
          const response = await fetchCitiesByState({
            variables: { state: state.id },
          });

          return (response?.data?.citiesByState || []).map((city) => ({
            value: city.id,
            cityId: city.id,
            stateId: state.id,
            label: formatLocationLabel(city.cityName, state.stateName),
          }));
        }),
      );

      const currentLocation =
        row?.city?.id && row?.state?.id
          ? [
              {
                value: row.city.id,
                cityId: row.city.id,
                stateId: row.state.id,
                label: formatLocationLabel(
                  row.city.cityName,
                  row.state.stateName,
                ),
              },
            ]
          : [];
      const mergedLocations = [...currentLocation, ...locations.flat()];
      const uniqueLocations = mergedLocations.filter(
        (location, index, allLocations) =>
          allLocations.findIndex((item) => item.cityId === location.cityId) ===
          index,
      );

      if (isMounted) setLocationOptions(uniqueLocations);
    };

    loadLocations();

    return () => {
      isMounted = false;
    };
  }, [fetchCitiesByState, isOpen, row, states]);

  function validateField(name, value, nextFormData = formData) {
    const numberValue = Number(value);

    if (name === 'totalQty') {
      if (!numberValue || numberValue <= 0)
        return 'Please enter total quantity.';
      if (numberValue > MAX_QUANTITY)
        return `Total Quantity cannot exceed ${MAX_QUANTITY}`;
      if (isOffer && Number(nextFormData.minimumQty) > numberValue) {
        return 'Total quantity should be greater than or equal to MOQ.';
      }
    }

    if (name === 'minimumQty' && isOffer) {
      if (!numberValue || numberValue <= 0) return 'Please enter MOQ.';
      if (numberValue > MAX_QUANTITY)
        return `MOQ cannot exceed ${MAX_QUANTITY}`;
      if (numberValue > Number(nextFormData.totalQty)) {
        return 'MOQ should not be greater than total quantity.';
      }
    }

    if (name === 'rate') {
      if (!numberValue || numberValue <= 0) return 'Please enter rate.';
      if (numberValue > MAX_RATE) return `Rate cannot exceed ${MAX_RATE}`;
    }

    if (name === 'validityInHours' && (!numberValue || numberValue <= 0)) {
      return 'Please enter validity hours.';
    }

    if (name === 'cityId' && !value) return 'Please select location.';

    return '';
  }

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'stateId' ? { cityId: '' } : {}),
    }));
    setErrors((prev) => {
      const nextFormData = { ...formData, [name]: value };
      return {
        ...prev,
        [name]: validateField(name, value, nextFormData),
        ...(isOffer && ['totalQty', 'minimumQty'].includes(name)
          ? {
              totalQty: validateField(
                'totalQty',
                nextFormData.totalQty,
                nextFormData,
              ),
              minimumQty: validateField(
                'minimumQty',
                nextFormData.minimumQty,
                nextFormData,
              ),
            }
          : {}),
      };
    });
  };

  const handleLocationChange = (selectedLocation) => {
    setFormData((prev) => ({
      ...prev,
      cityId: selectedLocation?.cityId || '',
      stateId: selectedLocation?.stateId || '',
    }));
    setErrors((prev) => ({
      ...prev,
      cityId: selectedLocation ? '' : 'Please select location.',
    }));
  };

  const validateForm = () => {
    const nextErrors = {
      totalQty: validateField('totalQty', formData.totalQty),
      rate: validateField('rate', formData.rate),
      validityInHours: validateField(
        'validityInHours',
        formData.validityInHours,
      ),
      cityId: validateField('cityId', formData.cityId),
    };

    if (isOffer) {
      nextErrors.minimumQty = validateField('minimumQty', formData.minimumQty);
    }

    setErrors(nextErrors);
    return Object.values(nextErrors).some(Boolean);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (validateForm()) return;

    const validityInHours = Number(formData.validityInHours);
    const validityStartTime = Date.now();
    const validityExpireTime =
      validityStartTime + validityInHours * 60 * 60 * 1000;

    const commonVariables = {
      id: row?.id,
      totalQty: Number(formData.totalQty),
      rate: Number(formData.rate),
      validityInHours,
      validityStartTime: String(validityStartTime),
      validityExpireTime: String(validityExpireTime),
      cityId: formData.cityId,
    };

    try {
      setSubmitting(true);
      if (!isEdit) {
        const statusResponse = isOffer
          ? await updateOfferStatus({
              variables: { id: row?.id, status: 'ACTIVE' },
            })
          : await updateDemandStatus({
              variables: { id: row?.id, status: 'ACTIVE' },
            });

        toast.success(
          statusResponse?.data?.[
            isOffer ? 'adminUpdateOfferStatus' : 'adminUpdateDemandStatus'
          ] || `${isOffer ? 'Offer' : 'Demand'} reactivated successfully`,
        );
        onSuccess?.();
        onClose?.();
        return;
      }

      if (isOffer) {
        await updateOffer({
          variables: {
            ...commonVariables,
            minimumQty: Number(formData.minimumQty),
          },
        });
      } else {
        await updateDemand({ variables: commonVariables });
      }

      toast.success(`${isOffer ? 'Offer' : 'Demand'} updated successfully`);
      onSuccess?.();
      onClose?.();
    } catch (error) {
      toast.error(getGraphQLErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const modalActionLabel = isEdit ? 'Update' : 'Reactivate';
  const submittingLabel = isEdit ? 'Updating...' : 'Reactivating...';

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={onClose}>
          {isEdit ? 'Edit' : 'Reactivate'} {isOffer ? 'Offer' : 'Demand'}
        </ModalHeader>
        <ModalBody>
          <div className="border rounded p-3 mb-3 bg-light">
            <div className="fw-semibold">
              {grade?.name || `${isOffer ? 'Offer' : 'Demand'} #${row?.id}`}
            </div>
            <div className="text-muted f-12">
              {grade?.productSubCategoryManufacturer || ''}
            </div>
          </div>

          <Row>
            <Col sm={isOffer ? 6 : 12}>
              <FormGroup>
                <Label>
                  Total Quantity <span className="text-danger">*</span>
                </Label>
                <div className="input-group">
                  <Input
                    name="totalQty"
                    type="number"
                    min="1"
                    step="1"
                    invalid={!!errors.totalQty}
                    value={formData.totalQty}
                    onChange={handleChange}
                  />
                  <span className="input-group-text">{quantityUnit}</span>
                </div>
                {errors.totalQty && (
                  <div className="text-danger f-12 mt-1">{errors.totalQty}</div>
                )}
              </FormGroup>
            </Col>
            {isOffer && (
              <Col sm={6}>
                <FormGroup>
                  <Label>
                    MOQ <span className="text-danger">*</span>
                  </Label>
                  <div className="input-group">
                    <Input
                      name="minimumQty"
                      type="number"
                      min="1"
                      step="1"
                      invalid={!!errors.minimumQty}
                      value={formData.minimumQty}
                      onChange={handleChange}
                    />
                    <span className="input-group-text">{quantityUnit}</span>
                  </div>
                  {errors.minimumQty && (
                    <div className="text-danger f-12 mt-1">
                      {errors.minimumQty}
                    </div>
                  )}
                </FormGroup>
              </Col>
            )}
          </Row>

          <Row>
            <Col sm={6}>
              <FormGroup>
                <Label>
                  Rate <span className="text-danger">*</span>
                </Label>
                <div className="input-group">
                  <span className="input-group-text">₹</span>
                  <Input
                    name="rate"
                    type="number"
                    min="0"
                    max={MAX_RATE}
                    step="0.01"
                    invalid={!!errors.rate}
                    value={formData.rate}
                    onChange={handleChange}
                  />
                  <span className="input-group-text">
                    /{row?.quantityUnit || 'KG'}
                  </span>
                </div>
                {errors.rate && (
                  <div className="text-danger f-12 mt-1">{errors.rate}</div>
                )}
              </FormGroup>
            </Col>
            <Col sm={6}>
              <FormGroup>
                <Label>
                  Validity <span className="text-danger">*</span>
                </Label>
                <Input
                  name="validityInHours"
                  type="number"
                  min="1"
                  invalid={!!errors.validityInHours}
                  value={formData.validityInHours}
                  onChange={handleChange}
                />
                {errors.validityInHours && (
                  <div className="text-danger f-12 mt-1">
                    {errors.validityInHours}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col sm={12}>
              <FormGroup>
                <Label>
                  Location <span className="text-danger">*</span>
                </Label>
                <Select
                  className="react-select"
                  classNamePrefix="react-select"
                  isClearable
                  isSearchable
                  isLoading={statesLoading || citiesLoading}
                  options={locationOptions}
                  placeholder="Select Location"
                  value={
                    locationOptions.find(
                      (location) =>
                        String(location.cityId) === String(formData.cityId),
                    ) || null
                  }
                  onChange={handleLocationChange}
                />
                {errors.cityId && (
                  <div className="text-danger f-12 mt-1">{errors.cityId}</div>
                )}
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline type="button" onClick={onClose}>
            Discard
          </Button>
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? submittingLabel : modalActionLabel}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default ReactivateListingModal;
